import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const Navigation = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const navItems = [
    { path: '/role-mastery', label: 'Role Mastery', icon: 'Award' },
    { path: '/content-production', label: 'Content Production', icon: 'PenTool' },
    { path: '/workflow-engine', label: 'Workflow Engine', icon: 'Cpu' },
    { path: '/engagement-management', label: 'Engagement', icon: 'MessageCircle' },
    { path: '/reporting-analytics', label: 'Analytics', icon: 'PieChart' },
    { path: '/tools-templates', label: 'Tools & Assets', icon: 'Briefcase' },
    { path: '/remote-work', label: 'Remote Plan', icon: 'Wifi' },
    { path: '/multi-brand-center', label: 'Multi-Brand', icon: 'Grid' },
    { path: '/content-pipeline', label: 'Pipeline', icon: 'Trello' },
    { path: '/trend-research', label: 'Trend Engine', icon: 'Zap' }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Mobile Navigation Component
  const MobileNavigation = () => (
    <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/30 mobile-safe-area">
      {/* Mobile Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center">
          <motion.div 
            initial={{ scale: 0 }} 
            animate={{ scale: 1 }} 
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-10 h-10 gradient-brown rounded-xl mb-0 shadow-apple-card flex items-center justify-center text-gold"
          >
            <SafeIcon name="Command" className="w-5 h-5" />
          </motion.div>
          <div className="ml-3">
            <h1 className="text-lg font-black text-brown tracking-tight">MindGarden</h1>
            <p className="text-xs font-semibold text-gold uppercase tracking-wider">Ops Suite</p>
          </div>
        </div>
        
        <motion.button
          onClick={toggleMobileMenu}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="w-10 h-10 flex items-center justify-center rounded-xl bg-beige/50 touch-target"
        >
          <SafeIcon name={isMobileMenuOpen ? 'X' : 'Menu'} className="w-5 h-5 text-brown" />
        </motion.button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-1">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="relative block group"
                  >
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`relative flex items-center px-4 py-3 rounded-xl transition-all duration-300 touch-target ${
                        isActive ? 'text-brown font-semibold bg-gradient-to-r from-gold/20 to-apache/20 border border-gold/30' : 'text-brown/60 hover:text-brown font-medium'
                      }`}
                    >
                      <motion.div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
                          isActive ? 'bg-gradient-to-br from-gold to-apache text-white' : 'bg-beige/50 text-brown/40'
                        }`}
                      >
                        <SafeIcon name={item.icon} className="w-4 h-4" />
                      </motion.div>
                      <span className="text-sm">{item.label}</span>
                    </motion.div>
                  </Link>
                );
              })}
              
              {/* Mobile User Profile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="pt-4 mt-4 border-t border-white/20"
              >
                <div className="p-3 glass-card rounded-xl">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full gradient-brown flex items-center justify-center text-xs font-bold text-gold shadow-apple-card">
                      MG
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-bold text-brown">Admin User</div>
                      <div className="text-xs text-brown/60">Standard Access</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Desktop Navigation (unchanged)
  const DesktopNavigation = () => (
    <nav className="hidden lg:flex fixed left-0 top-0 h-screen w-72 glass-card flex flex-col z-50 font-sf-pro">
      {/* Header with enhanced branding */}
      <div className="p-8 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/10 to-apache/10 rounded-bl-3xl"></div>
        <div className="relative">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-12 h-12 gradient-brown rounded-2xl mb-4 shadow-apple-card flex items-center justify-center text-gold"
          >
            <SafeIcon name="Command" className="w-6 h-6" />
          </motion.div>
          <h1 className="text-2xl font-black text-brown tracking-tight mb-1">MindGarden</h1>
          <p className="text-xs font-semibold text-gold uppercase tracking-wider">Ops Suite v2.0</p>
        </div>
      </div>

      {/* Navigation items with Apple-style interactions */}
      <div className="flex-1 overflow-y-auto py-4 px-6 space-y-2">
        {navItems.map((item, index) => {
          const isActive = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path} className="relative block group">
              <AnimatePresence>
                {isActive && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute inset-0 bg-gradient-to-r from-gold/20 to-apache/20 rounded-xl border border-gold/30"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </AnimatePresence>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive ? 'text-brown font-semibold' : 'text-brown/60 hover:text-brown font-medium'
                }`}
              >
                <motion.div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
                    isActive ? 'bg-gradient-to-br from-gold to-apache text-white' : 'bg-beige/50 text-brown/40 group-hover:bg-gold/20 group-hover:text-gold'
                  }`}
                >
                  <SafeIcon name={item.icon} className="w-4 h-4" />
                </motion.div>
                <span className="text-sm">{item.label}</span>

                {/* Hover indicator */}
                <motion.div
                  className="absolute right-2 w-1 h-6 bg-gold rounded-full opacity-0 group-hover:opacity-100"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.2 }}
                />
              </motion.div>
            </Link>
          );
        })}
      </div>

      {/* User profile with enhanced styling */}
      <div className="p-6 border-t border-white/20">
        <motion.div whileHover={{ scale: 1.02 }} className="p-4 glass-card rounded-2xl cursor-pointer">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full gradient-brown flex items-center justify-center text-sm font-bold text-gold shadow-apple-card">
              MG
            </div>
            <div className="ml-3">
              <div className="text-sm font-bold text-brown">Admin User</div>
              <div className="text-xs text-brown/60">Standard Access</div>
            </div>
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              className="ml-auto"
            >
              <SafeIcon name="ChevronRight" className="w-4 h-4 text-brown/40" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </nav>
  );

  return (
    <>
      <MobileNavigation />
      <DesktopNavigation />
    </>
  );
};

export default Navigation;